<template>
  <article
    class="instruction-step-card-v2"
    :class="[`instruction-step-card-v2--${draftResult}`, { 'instruction-step-card-v2--employee': isEmployee }]"
  >
    <InspectionStepBody
      :category="category"
      :inspector-action="inspectorAction"
      :look-for="lookFor"
      :pass-criteria="passCriteria"
      :fail-criteria="failCriteria"
      :timestamp-ref="timestampRef"
      :video-jump-url="videoJumpUrl"
      :step-index="stepIndex"
      :step-count="stepCount"
      :show-play="showPlay"
      @timestamp-click="emit('timestamp-click', $event)"
    />

    <div
      class="instruction-step-card-v2__verdict"
      :class="{ 'instruction-step-card-v2__verdict--employee': isEmployee }"
    >
      <StepVerdictBar
        :value="draftResult"
        :show-buttons="!isEmployee"
        :read-only="isLocked"
        :disabled="saving"
        :aria-label="verdictAriaLabel"
        @change="onChange"
      />

      <p v-if="auditStamp" class="instruction-step-card-v2__stamp" data-testid="step-audit-stamp">
        {{ auditStamp }}
      </p>

      <div v-if="showCommentRead" class="instruction-step-card-v2__comment-read">
        <span class="instruction-step-card-v2__comment-label">Inspector comment</span>
        <p class="instruction-step-card-v2__comment-text" data-testid="step-comment-text">{{ savedComment }}</p>
      </div>

      <template v-if="!isLocked">
        <button
          v-if="showAddCommentLink"
          type="button"
          class="instruction-step-card-v2__add-comment"
          data-testid="step-add-comment"
          @click="commentOpen = true"
        >
          Add comment (optional)
        </button>

        <div v-if="showCommentBox" class="instruction-step-card-v2__comment">
          <label class="instruction-step-card-v2__comment-label" :for="commentId">
            {{ isFailDraft ? "Comment (required)" : "Comment (optional)" }}
          </label>
          <textarea
            :id="commentId"
            v-model="draftComment"
            class="instruction-step-card-v2__textarea"
            rows="3"
            :required="isFailDraft"
            :aria-required="isFailDraft ? 'true' : 'false'"
            :disabled="saving"
            data-testid="step-comment-input"
          />
          <div class="instruction-step-card-v2__comment-actions">
            <button
              type="button"
              class="instruction-step-card-v2__save"
              :disabled="!canSave || saving"
              data-testid="step-comment-save"
              @click="onSave"
            >
              Save
            </button>
            <button
              v-if="isUnsavedFailDraft"
              type="button"
              class="instruction-step-card-v2__cancel"
              data-testid="step-comment-cancel"
              @click="resetDraft"
            >
              Cancel
            </button>
          </div>
        </div>
      </template>
    </div>
  </article>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import InspectionStepBody from "@components/InspectionStepBody/InspectionStepBody.vue";
import StepVerdictBar from "@components/StepVerdictBar/StepVerdictBar.vue";

let nextCommentId = 0;

const props = defineProps({
  category: { type: String, default: "knowledge" },
  inspectorAction: { type: String, default: "" },
  lookFor: { type: String, default: "" },
  passCriteria: { type: String, default: "" },
  failCriteria: { type: String, default: "" },
  timestampRef: { type: String, default: "" },
  videoJumpUrl: { type: String, default: "" },
  stepIndex: { type: Number, default: null },
  stepCount: { type: Number, default: null },
  showPlay: { type: Boolean, default: true },
  /** "inspector" records results; "employee" shows the recorded result read-only. */
  mode: {
    type: String,
    default: "inspector",
    validator: (value) => ["inspector", "employee"].includes(value),
  },
  /** The recorded result: "not_yet_inspected", "passed" or "failed". */
  result: {
    type: String,
    default: "not_yet_inspected",
    validator: (value) => ["not_yet_inspected", "passed", "failed"].includes(value),
  },
  /** The recorded inspector comment. */
  comment: { type: String, default: "" },
  inspectedByName: { type: String, default: "" },
  /** ISO timestamp of the recorded result. */
  inspectedAt: { type: String, default: "" },
  /** The inspection has its final decision, so the step can no longer be changed. */
  readOnly: { type: Boolean, default: false },
  /** A result is being saved. */
  saving: { type: Boolean, default: false },
});

const emit = defineEmits(["verdict", "timestamp-click"]);

const commentId = `instruction-step-card-v2-comment-${nextCommentId++}`;
const draftResult = ref(props.result);
const draftComment = ref(props.comment ?? "");
const commentOpen = ref(Boolean(props.comment));

function resetDraft() {
  draftResult.value = props.result;
  draftComment.value = props.comment ?? "";
  commentOpen.value = Boolean(props.comment);
}

watch(() => [props.result, props.comment], resetDraft);

const isEmployee = computed(() => props.mode === "employee");
const isLocked = computed(() => props.readOnly || isEmployee.value);
const savedComment = computed(() => (props.comment ?? "").trim());
const showCommentRead = computed(() => {
  if (savedComment.value === "") return false;
  return isEmployee.value ? props.result === "failed" : props.readOnly;
});
const trimmedDraft = computed(() => draftComment.value.trim());
const isFailDraft = computed(() => draftResult.value === "failed");
const isUnsavedFailDraft = computed(() => isFailDraft.value && props.result !== "failed");
const verdictAriaLabel = computed(() =>
  props.stepIndex != null ? `Result of step ${props.stepIndex}` : "Step result"
);

const showAddCommentLink = computed(
  () => draftResult.value === "passed" && props.result === "passed" && !commentOpen.value
);
const showCommentBox = computed(() => isFailDraft.value || (draftResult.value === "passed" && commentOpen.value));

const canSave = computed(() => {
  if (isFailDraft.value) {
    return trimmedDraft.value !== "" && (props.result !== "failed" || trimmedDraft.value !== savedComment.value);
  }
  return trimmedDraft.value !== savedComment.value;
});

function formatStamp(iso) {
  const date = new Date(iso);
  if (!iso || Number.isNaN(date.getTime())) return "";
  const day = date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const time = date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return `${day} · ${time.replace(/ /g, " ")}`;
}

const auditStamp = computed(() => {
  if (props.result === "not_yet_inspected") return "";
  return [props.inspectedByName, formatStamp(props.inspectedAt)].filter(Boolean).join(" · ");
});

function onChange(next) {
  if (next === draftResult.value) return;
  if (next === "passed") {
    if (props.result === "passed") resetDraft();
    else emit("verdict", { result: "passed", comment: "" });
    return;
  }
  draftResult.value = "failed";
  draftComment.value = props.result === "failed" ? props.comment ?? "" : "";
  commentOpen.value = true;
}

function onSave() {
  if (!canSave.value) return;
  emit("verdict", { result: draftResult.value, comment: trimmedDraft.value });
}
</script>

<style scoped>
.instruction-step-card-v2 {
  display: flex;
  flex-direction: column;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  background: #ffffff;
  font-family: Inter, sans-serif;
}

.instruction-step-card-v2__verdict {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 16px 16px 20px;
  border-top: 1px solid #f2f4f7;
}

.instruction-step-card-v2--employee {
  position: relative;
  overflow: hidden;
}

.instruction-step-card-v2--employee::before {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 4px;
  background: #d0d5dd;
  content: "";
}

.instruction-step-card-v2--employee.instruction-step-card-v2--passed::before {
  background: #17b26a;
}

.instruction-step-card-v2--employee.instruction-step-card-v2--failed::before {
  background: #f04438;
}

.instruction-step-card-v2__verdict--employee {
  flex-flow: row wrap;
  align-items: center;
  gap: 10px 8px;
  padding: 12px 16px 12px 20px;
  border-top-color: #eaecf0;
  background: #f9fafb;
}

.instruction-step-card-v2__verdict--employee .instruction-step-card-v2__comment-read {
  flex: 0 0 100%;
}

.instruction-step-card-v2__stamp {
  margin: 0;
  color: #667085;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
}

.instruction-step-card-v2__comment,
.instruction-step-card-v2__comment-read {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.instruction-step-card-v2__comment-label {
  color: #344054;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.instruction-step-card-v2__comment-text {
  margin: 0;
  color: #475467;
  font-size: 14px;
  line-height: 20px;
  white-space: pre-wrap;
}

.instruction-step-card-v2__textarea {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  background: #ffffff;
  color: #101828;
  font-family: inherit;
  font-size: 14px;
  line-height: 20px;
  resize: vertical;
  box-sizing: border-box;
}

.instruction-step-card-v2__textarea:focus-visible {
  outline: 2px solid #2c0cfa;
  outline-offset: 1px;
}

.instruction-step-card-v2--failed .instruction-step-card-v2__textarea {
  border-color: #f04438;
}

.instruction-step-card-v2__comment-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.instruction-step-card-v2__add-comment,
.instruction-step-card-v2__cancel {
  align-self: flex-start;
  padding: 0;
  border: 0;
  background: none;
  color: #2c0cfa;
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  cursor: pointer;
}

.instruction-step-card-v2__add-comment:focus-visible,
.instruction-step-card-v2__cancel:focus-visible,
.instruction-step-card-v2__save:focus-visible {
  outline: 2px solid #2c0cfa;
  outline-offset: 2px;
}

.instruction-step-card-v2__save {
  padding: 6px 14px;
  border: 1px solid #7f56d9;
  border-radius: 8px;
  background: #7f56d9;
  color: #ffffff;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  cursor: pointer;
}

.instruction-step-card-v2__save:disabled {
  border-color: #eaecf0;
  background: #f2f4f7;
  color: #667085;
  cursor: not-allowed;
}
</style>
